import { useMemo } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import { traverse } from '@atlaskit/adf-utils/traverse';
import { expVal } from '@confluence/feature-experiments';

import {
	type ContentExtensionMetrics,
	retrieveContentExtensionMetricsFromNode,
} from '@confluence/view-content-perf-metrics/entry-points/extensionAnalyticsHelpers';

export type AdfStats = {
	contentExtensionMetrics: ContentExtensionMetrics;
	nodeCountByType: Record<string, number>;
	totalNodeCount: number;
};

type CustomPerfData = { contentExtensionMetrics?: ContentExtensionMetrics } | null | undefined;

export function useAdfStats(adfDocument: DocNode, customPerfData?: CustomPerfData): AdfStats {
	const shouldRetrieveContentExtensionMetrics = Boolean(customPerfData);

	const adfStats = useMemo(() => {
		const contentExtensionMetrics: ContentExtensionMetrics = {
			contentExtensionCount: {
				connect: {},
				forge: {},
			},
			contentExtensionKeys: {
				connect: [],
				forge: [],
			},
			hasExtensions: false,
		};
		const nodeCountByType: Record<string, number> = {};
		let totalNodeCount = 0;

		traverse(adfDocument, {
			any: (node, parent) => {
				nodeCountByType[node.type] = (nodeCountByType[node.type] || 0) + 1;
				totalNodeCount++;

				// This is the alternative to `retrieveContentExtensionMetrics` which avoids one more parsing of the ADF string into JSON.
				if (shouldRetrieveContentExtensionMetrics) {
					// Note: Unlike the old `retrieveContentExtensionMetrics` which only considers the top-level content of the document, t
					// his considers the whole document.
					if (expVal('cc_complexit_fe_nested_extension_metrics', 'isEnabled', false)) {
						retrieveContentExtensionMetricsFromNode(node, contentExtensionMetrics);
					} else {
						if (parent?.node?.type === 'doc') {
							retrieveContentExtensionMetricsFromNode(node, contentExtensionMetrics);
						}
					}
				}
			},
		});

		return {
			contentExtensionMetrics,
			nodeCountByType,
			totalNodeCount,
		};
	}, [adfDocument, shouldRetrieveContentExtensionMetrics]);

	if (shouldRetrieveContentExtensionMetrics) {
		customPerfData!.contentExtensionMetrics = adfStats.contentExtensionMetrics;
	}

	return adfStats;
}
